/**
 * SPEND. What the business paid out, so that what a channel cost is a number rather than a dash.
 *
 * `docs/plan-crm-attribution.md` section 8 state 3. Report A has counted cost since state 2 and
 * read zero every time, because `expenses` had no screen and therefore no rows: every kill rule
 * that turns on money was counting against nothing at all. This is the way in, and it is a small
 * typing form over four audited endpoints on a table that already exists.
 *
 * IT IS REACHED FROM SOURCES AND NOT FROM MONEY, and that is a decision about what each screen is.
 * Money is what households owe, read from a desk with a recent sign-in behind it, and it has no
 * tabs to add one to. Spend is the other direction entirely and needs no step-up at all, because
 * nothing on it is a balance or a code. It is one press from the report it feeds, which is where
 * somebody looking at a dash in the cost column is standing when they realise nothing was typed.
 *
 * THE CHANNEL IS NULLABLE AND MOST ROWS LEAVE IT EMPTY. Cover, a state filing fee, supplies, the
 * nominated free clean: none of them buys a channel. The one an operator gets wrong is the phone
 * bill, which is category Phone with NO channel, because `phone` is both an expense category and a
 * channel and a phone bill typed with both lands in cost per household for every call the business
 * ever took. The form says so where the box is, in the column's own words.
 *
 * AND ONLY MARKETING COUNTS. Report A's fifth statement filters on the category, so a letter batch
 * filed under anything else counts towards no channel at all. That is said under the box too,
 * rather than left to be discovered as a dash in a report six weeks later.
 *
 * AND A SPEND MAY NAME THE CAMPAIGN IT PAID FOR, which is the only way a campaign's spend is ever
 * more than $0.00: the register counts every live expense naming it. The choice lists the campaigns
 * the office still holds, in the Campaigns screen's own words, and a row paid for one thrown away
 * since keeps naming it, because the money was spent on it either way.
 *
 * NO PRICE, NO RATE AND NOTHING A CLIENT PAYS IS TYPED HERE. This is money going out. The card is
 * published and copied, never typed, and there is no field on this screen that could reach one.
 *
 * A DAY OF SPEND GROWTH SENT IS GROWTH'S (ops #47, `docs/seams/growth.md` section 6). Growth sends
 * the day again whenever the platform restates it, so a correction typed here would be overwritten
 * with nobody told: such a row says "From growth", opens on the day it was paid and its notes alone,
 * and has no discard. The day paid is the office's because the card charge is recorded by stamping
 * it on the days growth already reported, and never as a second row. The server refuses anything
 * else whatever this screen offers; this is the screen saying so first.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CHANNELS, EXPENSE_CATEGORIES, type Channel, type ExpenseCategory } from '@fk/shared';
import {
  ApiError,
  createExpense,
  discardExpense,
  listCampaigns,
  listExpenses,
  patchExpense,
  type CampaignRow,
  type Discard,
  type ExpenseList,
  type ExpenseRow,
  type Me,
} from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { DiscardControl } from '../components/Discard.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import {
  DataTable,
  FilterBar,
  FilterSelect,
  PanelRule,
  SidePanel,
  SplitLayout,
  useWideView,
  type DataColumn,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import {
  centsFromDollars,
  dollarsForInput,
  formatDay,
  isDay,
  money,
  orNull,
  shortDay,
} from '../format.ts';
import { CHANNEL_LABELS, EXPENSE_CATEGORY_LABELS } from '../labels.ts';
import {
  FIRST_PERIOD,
  PERIODS,
  PERIOD_LABELS,
  channelCostNote,
  customWindow,
  periodWindow,
  rollUpBesideTheTable,
  rollUpUnderTheCard,
  spendDayProblem,
  type DayWindow,
  type Period,
} from '../reports.ts';
import { FROM_GROWTH, campaignLabel } from './Campaigns.tsx';

/** What the panel of a day growth sent says above its two boxes. */
export const GROWTH_SPEND_WORDS =
  'From growth. Growth sends this day again whenever the platform restates it, so only the day it was paid and the notes are typed here.';

/** What the panel of a day growth sent says when saved as it opened, sending nothing. */
export const GROWTH_SPEND_UNCHANGED =
  'Neither the day paid nor the notes has changed, so nothing was sent.';

/** What a person has typed, before any of it is a row. Dollars stay a string until they parse. */
interface Typed {
  readonly incurredOn: string;
  readonly paidOn: string;
  readonly category: ExpenseCategory | '';
  readonly vendor: string;
  readonly description: string;
  readonly dollars: string;
  readonly channel: Channel | '';
  readonly notes: string;
  /** The campaign the money paid for, by id, or empty for none, which is most rows. */
  readonly campaignId: string;
}

/** A fresh form. The day is the server's own, never the phone's clock, and it is the usual answer. */
function emptyTyped(today: string): Typed {
  return {
    incurredOn: today,
    paidOn: '',
    category: '',
    vendor: '',
    description: '',
    dollars: '',
    channel: '',
    notes: '',
    campaignId: '',
  };
}

/** The row as it stands, so a correction opens on what is there rather than on an empty form. */
function typedFrom(row: ExpenseRow): Typed {
  return {
    incurredOn: row.incurredOn,
    paidOn: row.paidOn ?? '',
    category: row.category,
    vendor: row.vendor,
    description: row.description,
    dollars: dollarsForInput(row.amountCents),
    channel: row.channel ?? '',
    notes: row.notes ?? '',
    campaignId: row.campaignId ?? '',
  };
}

/** What the endpoints take, or the sentence saying which box is wrong. Never both. */
type Checked =
  | { readonly ok: true; readonly body: ReturnType<typeof bodyOf> }
  | { readonly ok: false; readonly problem: string };

function bodyOf(typed: Typed, category: ExpenseCategory, amountCents: number) {
  return {
    incurredOn: typed.incurredOn,
    paidOn: orNull(typed.paidOn),
    category,
    vendor: typed.vendor.trim(),
    description: typed.description.trim(),
    amountCents,
    channel: typed.channel === '' ? null : typed.channel,
    notes: orNull(typed.notes),
    campaignId: typed.campaignId === '' ? null : typed.campaignId,
  };
}

/**
 * Every refusal the server would make, made here first and in a sentence naming the box. The
 * server refuses all of them again, and the server is the one that counts.
 *
 * With one exception, which is `spendDayProblem` and is deliberate: a day next month is a day, so
 * the endpoint takes it, and every window this product reports over ends at the server's own day,
 * so nothing would ever show it again. That refusal is the screen's own, and its reasoning is in
 * `web/src/reports.ts` beside the periods that made it necessary.
 */
function check(typed: Typed, today: string): Checked {
  if (!isDay(typed.incurredOn)) return { ok: false, problem: 'Pick the day the money was spent.' };
  if (typed.paidOn !== '' && !isDay(typed.paidOn)) {
    return {
      ok: false,
      problem: 'The day it was paid is not a day. Leave it empty if it is unpaid.',
    };
  }
  const dayProblem = spendDayProblem(typed.incurredOn, typed.paidOn, today);
  if (dayProblem !== null) return { ok: false, problem: dayProblem };
  if (typed.category === '') return { ok: false, problem: 'Choose what the money was for.' };
  if (!typed.vendor.trim()) {
    return {
      ok: false,
      problem: 'Say who was paid, so this row can be found on a bank statement.',
    };
  }
  if (!typed.description.trim()) return { ok: false, problem: 'Say what the money bought.' };
  const amountCents = centsFromDollars(typed.dollars);
  if (amountCents === null || amountCents <= 0) {
    return { ok: false, problem: 'Type the amount in dollars, like 45 or 45.00.' };
  }
  return { ok: true, body: bodyOf(typed, typed.category, amountCents) };
}

export function Spend({ me }: { me: Me }) {
  const [period, setPeriod] = useState<Period>(FIRST_PERIOD);
  const [days, setDays] = useState<DayWindow>(() => periodWindow(FIRST_PERIOD, me.today));
  const [list, setList] = useState<ExpenseList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [opened, setOpened] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  /** The live campaigns, or null until they arrive. A register that never does leaves only none. */
  const [campaigns, setCampaigns] = useState<readonly CampaignRow[] | null>(null);
  const wide = useWideView();

  const chosen =
    period === 'custom' ? customWindow(days.from, days.to) : periodWindow(period, me.today);
  const from = chosen === null ? null : chosen.from;
  const to = chosen === null ? null : chosen.to;

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (from === null || to === null) return;
    let live = true;
    setError(null);
    setList(null);
    listExpenses(from, to)
      .then((given) => {
        if (live) setList(given);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The spend did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [from, to, attempt]);

  /**
   * THE CAMPAIGNS A SPEND CAN NAME, read beside the spend and never in its way. A register that
   * does not load costs the choice and the names on the rows, and nothing else: the spend is still
   * listed and still typed in, and a retry or the next save asks again.
   */
  useEffect(() => {
    let live = true;
    listCampaigns()
      .then((given) => {
        if (live) setCampaigns(given.campaigns);
      })
      .catch(() => {
        // Said under the box where the choice would be, rather than over the whole screen.
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const choosePeriod = (next: Period | '') => {
    if (next === '') return;
    if (next === 'custom' && chosen !== null) setDays(chosen);
    setPeriod(next);
  };

  /** Said once, then cleared by the next thing anybody does, the way every other screen does it. */
  const done = (sentence: string) => {
    setSaid(sentence);
    setOpened(null);
    reload();
  };

  const rows = list?.expenses ?? [];
  const openedRow = rows.find((row) => row.id === opened) ?? null;
  const beside = rollUpBesideTheTable(wide, openedRow !== null) ? openedRow : null;
  const total = rows.reduce((sum, row) => sum + row.amountCents, 0);

  return (
    <div>
      <ScreenTitle title="Spend" subtitle="What the business paid out, and what it bought." />

      <p className="mb-4 text-sm">
        <Link
          to="/sources"
          className="inline-flex min-h-11 desk:min-h-9 items-center text-navy/70 underline"
        >
          Where households came from
        </Link>
      </p>

      <TypeOneIn today={me.today} campaigns={campaigns} onSaved={done} />

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      <FilterBar>
        <FilterSelect
          id="spend-period"
          label="Period"
          value={period}
          onChange={choosePeriod}
          options={PERIODS}
          toLabel={(name) => PERIOD_LABELS[name]}
          className="md:w-[180px]"
        />
        {period === 'custom' ? (
          <>
            <DayField
              id="spend-from"
              label="From"
              value={days.from}
              onChange={(day) => setDays((current) => ({ ...current, from: day }))}
            />
            <DayField
              id="spend-to"
              label="To"
              value={days.to}
              onChange={(day) => setDays((current) => ({ ...current, to: day }))}
            />
          </>
        ) : null}
      </FilterBar>

      {chosen === null ? (
        <p className="rounded-xl border border-dashed border-navy/25 p-5 text-center text-sm text-navy/70">
          <span className={`mx-auto block ${MEASURE}`}>
            Type two days, the earlier one first, and the spend between them is listed.
          </span>
        </p>
      ) : error ? (
        <ErrorNotice title="The spend did not load" message={error.message} onRetry={reload} />
      ) : list === null ? (
        <Busy what="the spend" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="Nothing typed in for these days">
          <p>
            Every channel that costs money shows a dash in the cost column until a row is typed
            here. Add one above.
          </p>
        </EmptyNotice>
      ) : (
        <SplitLayout
          panel={
            beside === null ? undefined : (
              <OpenedSpend
                key={beside.id}
                row={beside}
                today={me.today}
                campaigns={campaigns}
                onClose={() => setOpened(null)}
                onSaved={done}
              />
            )
          }
        >
          <p className={`mb-2 text-xs text-navy/60 ${MEASURE}`}>
            {formatDay(list.from)} to {formatDay(list.to)}, newest first.{' '}
            {rows.length === 1 ? '1 row' : `${rows.length} rows`}, {money(total)} in all. Press one
            to correct it.
          </p>

          <DataTable
            label="Spend"
            groups={[{ key: 'spend', rows }]}
            columns={spendColumns(setOpened, campaigns)}
            rowKey={(row) => row.id}
            rowClassName={(row) =>
              row.id === opened
                ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                : ''
            }
            card={(row) => (
              <>
                <SpendCard
                  row={row}
                  campaigns={campaigns}
                  opened={row.id === opened}
                  onOpen={() => setOpened(row.id)}
                />
                {/*
                 * The same rule the channel roll-up uses, and the same two functions: below md the
                 * split lays its panel out after the whole list, so a correction opened from a card
                 * would be drawn a long way under the finger that asked for it.
                 */}
                {rollUpUnderTheCard(wide, row.id === opened) ? (
                  <div className="mt-3">
                    <OpenedSpend
                      row={row}
                      today={me.today}
                      campaigns={campaigns}
                      onClose={() => setOpened(null)}
                      onSaved={done}
                    />
                  </div>
                ) : null}
              </>
            )}
          />

          {rows.length >= list.limit ? (
            <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
              This is the first {list.limit} rows of that window, which is the most one read answers
              with. Choose a shorter period to see the rest.
            </p>
          ) : null}

          <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
            Only marketing spend with a channel on it counts towards what that channel cost on the
            Sources report. Everything else is money out and nothing more.
          </p>
        </SplitLayout>
      )}
    </div>
  );
}

/** A day in the filter bar, shaped like the selects beside it rather than like a form field. */
function DayField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2.5">
      <label htmlFor={id} className="text-xs leading-4 tracking-wide text-navy/55 uppercase">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 text-navy md:w-44"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The list, in two shapes. Below md it is not a table at all.
 * ------------------------------------------------------------------ */

/** What the channel cell says on a row that buys none, which is most of them. */
const NO_CHANNEL = 'None';

function channelCell(channel: Channel | null): string {
  return channel === null ? NO_CHANNEL : CHANNEL_LABELS[channel];
}

/** What a row says, and what its choice says, when the campaign it names is not in the register. */
const GONE_CAMPAIGN = 'A campaign no longer in the register';

/**
 * WHICH CAMPAIGN A ROW NAMES, in the words the row says it in. The register sends only campaigns
 * the office still holds, so a row paid for one thrown away since names an id nothing here can put
 * a name to, and it says so rather than guessing or going quiet. Until the register arrives there
 * is no name to give at all, so the row says only that it names one.
 */
function campaignWords(id: string, campaigns: readonly CampaignRow[] | null): string {
  if (campaigns === null) return 'Names a campaign';
  const found = campaigns.find((campaign) => campaign.id === id);
  return found === undefined ? GONE_CAMPAIGN : `Campaign: ${found.name}`;
}

/** A campaign as a choice: the Campaigns screen's own label, or what is known of one it lacks. */
function campaignChoice(id: string, campaigns: readonly CampaignRow[] | null): string {
  const found = campaigns?.find((campaign) => campaign.id === id);
  if (found !== undefined) return campaignLabel(found);
  return campaigns === null ? 'The campaign this spend names' : GONE_CAMPAIGN;
}

/**
 * THE WIDTHS ARE SHARES, NOT PIXELS, for the reason `screens/Money.tsx` sets out at length: a
 * fixed table takes the greater of its own width and the sum of its columns, so pixel widths that
 * add up to more than the room the rail leaves push the last columns off an edge with no way to
 * reach them. Five columns, because the description rides under the vendor rather than taking a
 * sixth: who was paid and what it bought are one fact on a bank statement. The campaign rides under
 * the channel for the same reason: the channel and the batch the money bought are one fact too.
 */
function spendColumns(
  open: (id: string) => void,
  campaigns: readonly CampaignRow[] | null,
): readonly DataColumn<ExpenseRow>[] {
  return [
    {
      key: 'incurred',
      header: 'Spent',
      width: '14%',
      cellClassName: 'text-navy/75',
      // Short, because the column is a sixth of a table the rail has already narrowed.
      cell: (row) => shortDay(row.incurredOn),
    },
    {
      key: 'category',
      header: 'For',
      width: '16%',
      cell: (row) => <Pill>{EXPENSE_CATEGORY_LABELS[row.category]}</Pill>,
    },
    {
      key: 'vendor',
      header: 'Who was paid',
      width: '30%',
      cell: (row) => (
        <button
          type="button"
          onClick={() => open(row.id)}
          className="flex min-h-11 desk:min-h-9 w-full flex-col justify-center text-left"
        >
          <span className="font-serif text-base break-words">{row.vendor}</span>
          <span className="text-xs break-words text-navy/65">{row.description}</span>
          {row.growthSpendKey === null ? null : (
            <span className="text-xs text-navy/65">{FROM_GROWTH}</span>
          )}
        </button>
      ),
    },
    {
      key: 'channel',
      header: 'Channel',
      width: '22%',
      cellClassName: 'text-navy/75',
      cell: (row) => (
        <>
          <span className="block">{channelCell(row.channel)}</span>
          {channelCostNote(row.category, row.channel) === null ? null : (
            <span className="block text-xs text-navy/60">Not counted as cost</span>
          )}
          {row.campaignId === null ? null : (
            <span className="block text-xs text-navy/60">
              {campaignWords(row.campaignId, campaigns)}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '18%',
      cell: (row) => <span className="font-semibold">{money(row.amountCents)}</span>,
    },
  ];
}

function SpendCard({
  row,
  campaigns,
  opened,
  onOpen,
}: {
  row: ExpenseRow;
  campaigns: readonly CampaignRow[] | null;
  opened: boolean;
  onOpen: () => void;
}) {
  return (
    <button type="button" onClick={onOpen} className="block w-full text-left">
      <Card className={opened ? 'border-gold' : ''}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-serif text-lg leading-tight">{row.vendor}</h2>
          <Pill>{EXPENSE_CATEGORY_LABELS[row.category]}</Pill>
          {row.growthSpendKey === null ? null : <Pill>{FROM_GROWTH}</Pill>}
        </div>
        <p className="mt-1 text-sm text-navy/75">{row.description}</p>
        <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm">
          <span>{formatDay(row.incurredOn)}</span>
          <span className="font-semibold">{money(row.amountCents)}</span>
        </div>
        <p className="mt-1 text-xs text-navy/60">
          {row.channel === null
            ? 'This buys no channel.'
            : `Channel: ${CHANNEL_LABELS[row.channel]}.`}
          {row.paidOn === null ? ' Not marked paid.' : ` Paid ${formatDay(row.paidOn)}.`}
        </p>
        {row.campaignId === null ? null : (
          <p className="mt-1 text-xs text-navy/60">{campaignWords(row.campaignId, campaigns)}.</p>
        )}
        {channelCostNote(row.category, row.channel) === null ? null : (
          <p className="mt-1 text-xs text-navy/60">{channelCostNote(row.category, row.channel)}</p>
        )}
      </Card>
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Typing one in, and correcting one. The same nine boxes both times.
 * ------------------------------------------------------------------ */

function TypeOneIn({
  today,
  campaigns,
  onSaved,
}: {
  today: string;
  campaigns: readonly CampaignRow[] | null;
  onSaved: (sentence: string) => void;
}) {
  const [typed, setTyped] = useState<Typed>(() => emptyTyped(today));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    const answer = check(typed, today);
    if (!answer.ok) {
      setProblem(answer.problem);
      return;
    }
    setSaving(true);
    try {
      await createExpense(answer.body);
      setTyped(emptyTyped(today));
      onSaved('That spend is recorded.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That spend was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-5 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Type in a spend
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={problem} />
          </div>
        ) : null}
        <SpendFields
          idPrefix="new-spend"
          typed={typed}
          today={today}
          campaigns={campaigns}
          onChange={setTyped}
        />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'Record this spend'}
        </Button>
      </form>
    </details>
  );
}

/** The nine boxes, written once, so the form and the correction cannot say different things. */
function SpendFields({
  idPrefix,
  typed,
  today,
  campaigns,
  onChange,
}: {
  idPrefix: string;
  typed: Typed;
  /** The server's own day, which is the latest either date box will take. */
  today: string;
  /** The live campaigns, or null while they have not arrived. */
  campaigns: readonly CampaignRow[] | null;
  onChange: (next: Typed) => void;
}) {
  const field = (name: string) => `${idPrefix}-${name}`;
  const set = <Key extends keyof Typed>(key: Key, value: Typed[Key]) =>
    onChange({ ...typed, [key]: value });
  const warning =
    typed.category === ''
      ? null
      : channelCostNote(typed.category, typed.channel === '' ? null : typed.channel);
  const liveIds = (campaigns ?? []).map((campaign) => campaign.id);
  // A row naming a campaign the register does not send keeps it as a choice, so the box shows the
  // campaign the row names rather than reading as none while the row still names one.
  const campaignIds =
    typed.campaignId === '' || liveIds.includes(typed.campaignId)
      ? liveIds
      : [...liveIds, typed.campaignId];

  return (
    <>
      {/*
       * Both boxes stop at today. The calendar a browser draws will not offer a later day, and
       * `check` refuses one typed in by hand: every period this screen offers ends at the server's
       * day, so a row dated after it is recorded and then listed nowhere.
       */}
      <TextField
        id={field('incurred')}
        label="Day the money was spent"
        type="date"
        required
        value={typed.incurredOn}
        max={today}
        onChange={(value) => set('incurredOn', value)}
        hint="The day it was committed, which is the day every report counts it in. Today or earlier."
      />
      <TextField
        id={field('paid')}
        label="Day it was paid"
        type="date"
        value={typed.paidOn}
        max={today}
        onChange={(value) => set('paidOn', value)}
        hint="Leave it empty until the money has actually gone."
      />
      <SelectField
        id={field('category')}
        label="What it was for"
        required
        value={typed.category}
        onChange={(value) => set('category', value)}
        options={EXPENSE_CATEGORIES}
        toLabel={(value) => EXPENSE_CATEGORY_LABELS[value]}
        placeholder="Choose what it was for"
        hint="Only Marketing counts towards what a channel cost on the Sources report."
      />
      <TextField
        id={field('vendor')}
        label="Who was paid"
        required
        value={typed.vendor}
        onChange={(value) => set('vendor', value)}
        autoComplete="off"
        hint="The name on the bank statement, so this row can be reconciled against it."
      />
      <TextField
        id={field('description')}
        label="What the money bought"
        required
        value={typed.description}
        onChange={(value) => set('description', value)}
        autoComplete="off"
        hint='"500 letters, first batch" reads better in a year than "printing".'
      />
      <TextField
        id={field('amount')}
        label="Amount in dollars"
        required
        value={typed.dollars}
        onChange={(value) => set('dollars', value)}
        inputMode="numeric"
        autoComplete="off"
        placeholder="45.00"
        hint="Dollars and cents. No sign is needed."
      />
      <SelectField
        id={field('channel')}
        label="Channel this buys"
        value={typed.channel}
        onChange={(value) => set('channel', value)}
        options={CHANNELS}
        toLabel={(value) => CHANNEL_LABELS[value]}
        placeholder="None: this spend buys no channel"
        hint="Null where the spend buys no channel at all, which is most rows. The phone bill is the one to get right: it is Phone with no channel, because typed with the Phone channel it lands in the cost of every call this business ever took."
      />
      {warning ? <p className={`mb-4 -mt-3 text-xs text-navy/65 ${MEASURE}`}>{warning}</p> : null}
      <SelectField
        id={field('campaign')}
        label="Campaign this paid for"
        value={typed.campaignId}
        onChange={(value) => set('campaignId', value)}
        options={campaignIds}
        toLabel={(value) => campaignChoice(value, campaigns)}
        placeholder="None: this spend names no campaign"
        hint={
          campaigns === null
            ? 'The campaigns have not loaded, so none can be chosen yet.'
            : 'Optional. The letter batch or ad flight this money paid for, which is what the Campaigns screen counts as spent. Most spend names none.'
        }
      />
      <TextAreaField
        id={field('notes')}
        label="Anything to add"
        value={typed.notes}
        onChange={(value) => set('notes', value)}
        rows={2}
        hint="Optional. An invoice number, or what the money is expected to produce."
      />
    </>
  );
}

/** One row, opened: the office's in full, or growth's on the day it was paid and its notes. */
function OpenedSpend(props: {
  row: ExpenseRow;
  today: string;
  campaigns: readonly CampaignRow[] | null;
  onClose: () => void;
  onSaved: (sentence: string) => void;
}) {
  return props.row.growthSpendKey === null ? (
    <SpendPanel {...props} />
  ) : (
    <GrowthSpendPanel {...props} />
  );
}

/**
 * A DAY OF SPEND GROWTH SENT, OPENED: what growth sent, and the two fields the office keeps on it,
 * the day the card charge paid it and its notes. No discard: a day of spend goes away by growth
 * sending nothing for it. Once the day paid is stamped, growth's restatements of the day are noted
 * under the notes by the server and never replace the amount.
 */
function GrowthSpendPanel({
  row,
  today,
  campaigns,
  onClose,
  onSaved,
}: {
  row: ExpenseRow;
  today: string;
  campaigns: readonly CampaignRow[] | null;
  onClose: () => void;
  onSaved: (sentence: string) => void;
}) {
  const [paidOn, setPaidOn] = useState(row.paidOn ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (paidOn !== '' && !isDay(paidOn)) {
      setProblem('The day it was paid is not a day. Leave it empty if it is unpaid.');
      return;
    }
    const dayProblem = spendDayProblem(row.incurredOn, paidOn, today);
    if (dayProblem !== null) {
      setProblem(dayProblem);
      return;
    }
    // Each field goes only when the office changed it. The notes have two writers: growth notes a
    // figure it sent for a paid day under them, and a stamp sending the notes as this panel opened
    // would put back the notes from before that sentence (`docs/seams/growth.md` section 5).
    const changed: { paidOn?: string | null; notes?: string | null } = {};
    if (orNull(paidOn) !== row.paidOn) changed.paidOn = orNull(paidOn);
    if (orNull(notes) !== row.notes) changed.notes = orNull(notes);
    if (Object.keys(changed).length === 0) {
      setProblem(GROWTH_SPEND_UNCHANGED);
      return;
    }
    setSaving(true);
    try {
      await patchExpense(row.id, changed);
      onSaved('That is on the record, with what it said before.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SidePanel title={row.vendor} pills={<Pill>{FROM_GROWTH}</Pill>} onClose={onClose}>
      <p className="text-sm text-navy/75">{row.description}</p>
      <p className="text-sm text-navy/75">
        {formatDay(row.incurredOn)}, {money(row.amountCents)}.{' '}
        {row.channel === null
          ? 'This buys no channel.'
          : `Channel: ${CHANNEL_LABELS[row.channel]}.`}
        {row.campaignId === null ? '' : ` ${campaignWords(row.campaignId, campaigns)}.`}
      </p>
      <p className={`text-sm text-navy/75 ${MEASURE}`}>{GROWTH_SPEND_WORDS}</p>

      <form onSubmit={save} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <TextField
          id={`spend-${row.id}-paid`}
          label="Day it was paid"
          type="date"
          value={paidOn}
          max={today}
          onChange={setPaidOn}
          hint="The day on the card statement that paid it. Once it is stamped, a figure growth sends for this day is noted under the notes and never replaces the amount."
        />
        <TextAreaField
          id={`spend-${row.id}-notes`}
          label="Anything to add"
          value={notes}
          onChange={setNotes}
          rows={2}
          hint="Optional. The statement it was paid on, or anything else worth knowing."
        />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Save the day paid and the notes'}
        </Button>
      </form>
    </SidePanel>
  );
}

/**
 * One row, opened. A correction first, and the quiet discard under everything worth doing before
 * it: a discard is nobody's gap, so it is never coral and it never acts on the first press.
 */
function SpendPanel({
  row,
  today,
  campaigns,
  onClose,
  onSaved,
}: {
  row: ExpenseRow;
  today: string;
  campaigns: readonly CampaignRow[] | null;
  onClose: () => void;
  onSaved: (sentence: string) => void;
}) {
  const [typed, setTyped] = useState<Typed>(() => typedFrom(row));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    const answer = check(typed, today);
    if (!answer.ok) {
      setProblem(answer.problem);
      return;
    }
    setSaving(true);
    try {
      // The campaign goes only when it changed. The server asks whether a campaign is still held
      // only when one is sent, so a row naming one thrown away after it was paid for still takes a
      // correction to its amount; choosing none sends null, which takes the campaign off the row.
      const { campaignId, ...rest } = answer.body;
      await patchExpense(row.id, campaignId === row.campaignId ? rest : answer.body);
      onSaved('That correction is on the record, with what it said before.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That correction was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SidePanel title={row.vendor} onClose={onClose}>
      <form onSubmit={save} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <SpendFields
          idPrefix={`spend-${row.id}`}
          typed={typed}
          today={today}
          campaigns={campaigns}
          onChange={setTyped}
        />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Save this correction'}
        </Button>
      </form>

      <PanelRule />
      <DiscardControl
        idPrefix={`spend-${row.id}`}
        what="this spend"
        doneSentence="Discarded. It is off the list, and why is on the record."
        hint="It leaves the list and stops counting towards any channel. The record and the reason stay."
        onDiscard={(body: Discard) => discardExpense(row.id, body)}
        onDone={onSaved}
      />
    </SidePanel>
  );
}
